import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSandboxActions } from '@/components/WordPressMCPChat/useSandboxActions';
import {
  bindTechnicalAuditIssuePreparation,
  cancelChatHubTurn,
  compactHistory,
  deleteChatHubConversation,
  getChatHubConversation,
  isChatHubPreparationConflict,
  listChatHubConversations,
  prepareTechnicalAuditIssueChat,
  settleChatHubApproval,
  answerChatHubQuestion,
  settleTechnicalAuditApproval,
  streamChatHub,
  type TechnicalAuditIssuePreparation,
} from '@/services/chatHubService';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  CENTRAL_CHAT_HELP_TEXT,
  CENTRAL_CHAT_MODEL,
  CENTRAL_CHAT_MODES,
  CENTRAL_CHAT_PROVIDER,
  CENTRAL_CHAT_WORKFLOW_TILES,
  MODEL_CHOOSING_CONTEXT_TYPES,
} from './constants';
import {
  centralChatScopeKey,
  createCentralChatMessage,
  useCentralChatStore,
} from './centralChatStore';
import { buildCentralChatContext } from './context';
import {
  conversationPage,
  issuePage,
  pageScopeKey,
  resolvePageSession,
  type ActivePage,
} from './pageSessionKey';
import {
  forgetTaProposalDelivery,
  markTaProposalDelivered,
  registerTaApprovalSubmitter,
} from './taApprovalBridge';
import {
  parseRestoredTaProposal,
  parseTaProposal,
  restoredFromTaOutcome,
} from './taProposalTypes';
import { useQueryClient } from '@tanstack/react-query';
import { ISSUES_QUERY_KEY } from '@/hooks/useTechnicalAuditIssues';
import {
  beginClientTool,
  clientToolDeadline,
  settleClientTool,
  STALL_HEARTBEAT_LIMIT,
  STALL_MESSAGE,
  awaitingLabel,
  isHeartbeatEvent,
  isTurnCapped,
  resolveTool,
  settleTools,
  silenceStatus,
  startTool,
  stoppedContent,
  turnCapStatus,
  type ToolActivityEntry,
} from './chatActivity';
import {
  currentPageTools,
  getClientToolExecutor,
  postClientToolResult,
  registerClientToolResultPoster,
} from './clientToolBridge';
import { buildCentralChatHref, currentRoute, offeredActions } from './navigation';
import { writeApprovalFromPayload } from './WriteApprovalCard';
import { questionFromPayload } from './emjaeQuestion';
import type {
  AgilePlanRowResult,
  CentralChatContext,
  CentralChatIssueAttachment,
  CentralChatMessage,
  ChatHubConversation,
  ChatHubStreamEvent,
  ClientToolCall,
  ClientToolResult,
  TurnOutcome,
} from './types';
import { isAgilePlanApproval } from './types';
import type { CentralChatMode, CentralChatWorkflowTile } from './CentralChatArrowSurface';
import { DEFAULT_CHAT_ACCESS_MODE, type ChatAccessMode } from './chatAccessMode';

const AUDIT_ISSUE_ROUTE = '/audit/technical-issues';

/** A history row that knows which page its conversation belongs to, when known. */
export type ListedConversation = ChatHubConversation & {
  pageKey?: string;
  pageLabel?: string;
};

type ReadyIssuePreparation = Extract<TechnicalAuditIssuePreparation, { status: 'ready' }>;

interface IssuePreparationHandle {
  tenantId: string;
  issueId: string;
  controller: AbortController;
  promise: Promise<TechnicalAuditIssuePreparation>;
  preparation: TechnicalAuditIssuePreparation | null;
  rejected: boolean;
}

type BoundAuditIssueContext = {
  context_type: 'technical_audit_issue';
  issue_id: string;
  route: string;
  issue_type?: string;
  wordpress_id?: number | null;
  run_id?: string | null;
};

function boundAuditIssueFrom(
  ctx: Partial<CentralChatContext> | undefined,
): BoundAuditIssueContext | null {
  if (!ctx || ctx.context_type !== 'technical_audit_issue') return null;
  const issueId = ctx.issue_id;
  if (typeof issueId !== 'string' || !issueId) return null;
  const route = typeof ctx.route === 'string' && ctx.route ? ctx.route : AUDIT_ISSUE_ROUTE;
  return { context_type: 'technical_audit_issue', issue_id: issueId, route };
}

function withBoundAuditIssue(
  context: CentralChatContext,
  bound: BoundAuditIssueContext | null,
): CentralChatContext {
  if (!bound) return context;
  return {
    ...context,
    context_type: bound.context_type,
    issue_id: bound.issue_id,
    route: bound.route,
  };
}

interface CentralChatRuntimeValue {
  tenantId: string | null;
  /** The router pathname (no query string, no hash): which page Emjae is open on. */
  pathname: string;
  /** The active client's display name for starter prompts: the workspace name, else its website
   *  domain, else an unlinked tenant's domain; null when none is selected. Never an id. */
  clientName: string | null;
  sessionKey: string | null;
  /** The page whose MJ session is live now: its storage key and a short label. */
  activePage: ActivePage;
  contextLabel: string;
  draft: string;
  status: string;
  isStreaming: boolean;
  conversationLoading: boolean;
  activeConversationId: string | null;
  chatAccessMode: ChatAccessMode;
  messages: CentralChatMessage[];
  conversations: ListedConversation[];
  toolActivity: ToolActivityEntry[];
  /** When the live turn began, for the elapsed clock. Null between turns. */
  turnStartedAt: number | null;
  /** Page tool calls Eve handed to the browser, newest last. */
  clientToolCalls: ClientToolCall[];
  /** Entry point for a page tool_use — the stream calls it, and so may the
   *  WebMCP bridge for shapes the stream does not carry yet. */
  handleClientToolUse: (toolUse: { id: string; name: string; args: Record<string, unknown> }) => void;
  modes: CentralChatMode[];
  workflowTiles: CentralChatWorkflowTile[];
  helpText: string;
  bubbleOpen: boolean;
  /** Bumped by openBubble and openIssueChat; 0 at page load. Lets the mascot tell
   *  "just opened" from "opened state restored on reload". */
  openNonce: number;
  unreadCount: number;
  isFullChatRoute: boolean;
  setDraft: (draft: string) => void;
  setChatAccessMode: (mode: ChatAccessMode) => void;
  submit: (text: string, contextOverride?: Partial<CentralChatContext>) => void;
  stop: () => void;
  handleWorkflow: (tile: CentralChatWorkflowTile) => void;
  handleMode: (mode: CentralChatMode) => void;
  openConversation: (conversationId: string) => void;
  refreshConversations: () => void;
  deleteConversation: (conversationId: string) => void;
  startNew: () => void;
  openBubble: () => void;
  closeBubble: () => void;
  openFullChat: () => void;
  goHome: () => void;
  sandboxOutput: (payload: unknown) => void;
  settleWriteApproval: (decision: 'approve' | 'cancel') => void;
  /** Answer Emjae's pending question: an offered option, or typed text when it allows freeform. */
  answerQuestion: (answer: { optionId: string } | { text: string }) => void;
  setIssueContext: (attachment: CentralChatIssueAttachment) => void;
  clearIssueContext: () => void;
  activeIssue: CentralChatIssueAttachment | null;
  openIssueChat: (attachment: CentralChatIssueAttachment, draft: string) => void;
}

const CentralChatRuntimeContext = createContext<CentralChatRuntimeValue | null>(null);

/**
 * Why a turn is being aborted, carried on the turn rather than in a shared ref.
 * A new submit aborts the turn before it while that turn's catch is still
 * pending, so a single shared reason would be read by the wrong one.
 */
type ChatTurnEnding = 'user' | 'stalled' | 'idle' | 'replaced';

interface ChatTurn {
  controller: AbortController;
  ending: ChatTurnEnding | null;
  startedAt: number;
  /**
   * Resolves when this turn has finished tearing down. A page tool's answer
   * waits on it before re-entering the stream: the answer is produced while
   * the turn that asked for it is still closing, and posting into the same
   * conversation before its Eve cursor is saved would resume from a stale one
   * and replay the park instead of continuing past it.
   */
  settled: Promise<void>;
  markSettled: () => void;
}

/** A turn, with the handle its settlement is signalled through. */
function beginTurn(controller: AbortController, startedAt: number): ChatTurn {
  let markSettled = () => {};
  const settled = new Promise<void>((resolve) => {
    markSettled = resolve;
  });
  return { controller, ending: null, startedAt, settled, markSettled };
}

function unreadAssistantCount(
  messages: CentralChatMessage[],
  lastReadMessageId: string | null,
): number {
  if (!messages.length || !lastReadMessageId) return 0;
  const lastReadIndex = messages.findIndex((message) => message.id === lastReadMessageId);
  const unread = lastReadIndex >= 0 ? messages.slice(lastReadIndex + 1) : messages;
  return unread.filter(
    (message) => message.role === 'assistant' && Boolean(message.content.trim() || message.actions?.length),
  ).length;
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A site as a comparable key: no scheme, no "www.", no path, lower case. */
function siteKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[/?#].*$/, '');
}

/**
 * A client's name for starter prompts: the workspace's name, else its website domain; an unlinked
 * tenant is its domain. A blank or id-shaped name never shows. And it is only used when the
 * selection is the SAME client the chat answers about (its website matches the chat's tenant):
 * the workspace store and the tenant store are kept in step by a bridge, and if they ever differ
 * a button that names one client must not be answered about another. Otherwise null, and the
 * prompts say "this client".
 */
function clientDisplayName(
  selection: ReturnType<typeof useWorkspaceStore.getState>['selection'],
  chatTenantId: string | null,
): string | null {
  if (!selection || !chatTenantId) return null;
  const chatSite = siteKey(chatTenantId);
  if (selection.kind === 'tenant') {
    return siteKey(selection.tenantId) === chatSite ? selection.tenantId.trim() || null : null;
  }
  if (siteKey(selection.workspace.website) !== chatSite) return null;
  const name = (selection.workspace.name ?? '').trim();
  if (name && !UUID_SHAPE.test(name)) return name;
  return (selection.workspace.website ?? '').trim() || null;
}

export function CentralChatProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const tenantId = useTenantStore((state) => state.currentTenant);
  const clientSelection = useWorkspaceStore((state) => state.selection);
  const clientName = useMemo(() => clientDisplayName(clientSelection, tenantId), [clientSelection, tenantId]);
  // Tenant/user scope: what a session used to be keyed by, and still the scope
  // for tenant-wide UI (the rail open/closed) and the page→conversation index.
  const sessionKey = centralChatScopeKey(tenantId, user?.id);
  const store = useCentralChatStore();
  const [activeIssue, setActiveIssue] = useState<CentralChatIssueAttachment | null>(null);
  // Which page's session is live now. An issue-bound chat keys on the issue's
  // client page; otherwise the route. This drives the storage scope below, so
  // navigating to a different page swaps to that page's session.
  const baseContext = useMemo(
    () =>
      buildCentralChatContext({
        tenantId,
        pathname: location.pathname,
        searchParams: new URLSearchParams(location.search),
      }),
    // Use location.search (stable string) instead of searchParams object which changes every render
    [tenantId, location.pathname, location.search],
  );
  const activePage = useMemo<ActivePage>(
    () => resolvePageSession({ activeIssue, context: baseContext, pathname: location.pathname }),
    [activeIssue, baseContext, location.pathname],
  );
  // The per-page storage scope. Every session read/write below uses this; only
  // bubbleOpen and the page index keep using `sessionKey` (tenant scope).
  const pageScope = pageScopeKey(sessionKey, activePage.key);
  const session = store.getSession(pageScope);
  const chatAccessMode = store.getChatAccessMode(sessionKey, pageScope);
  const uiState = store.getUiState(sessionKey);
  const pages = store.getPages(sessionKey);
  const messages = session.messages;
  const [isStreaming, setIsStreaming] = useState(false);
  const [status, setStatus] = useState('');
  const [toolActivity, setToolActivity] = useState<ToolActivityEntry[]>([]);
  const [turnStartedAt, setTurnStartedAt] = useState<number | null>(null);
  const [conversations, setConversations] = useState<ChatHubConversation[]>([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const turnRef = useRef<ChatTurn | null>(null);
  // The Agile Plan card message an approval is being settled for: its results land on it, not on a new message.
  const agilePlanTargetRef = useRef<string | null>(null);
  // One settle at a time: set the moment a settle starts (before any await), so two clicks in the same tick send one request.
  const settlingRef = useRef(false);
  const parkedTurnModeRef = useRef<{ pageScope: string; mode: ChatAccessMode } | null>(null);
  const clientToolModeRef = useRef<Map<string, { pageScope: string; mode: ChatAccessMode }>>(new Map());
  const queryClient = useQueryClient();
  const issuePreparationRef = useRef<IssuePreparationHandle | null>(null);
  const boundAuditIssueRef = useRef<BoundAuditIssueContext | null>(null);
  const preparationSubmitRef = useRef<symbol | null>(null);

  // The ref is authoritative and the state only publishes it, because settling
  // a turn has to read the run it is settling in the same tick that it ends it.
  // Reading `toolActivity` there would see the render before the last tool.
  const toolActivityRef = useRef<ToolActivityEntry[]>([]);
  const applyTools = useCallback(
    (next: (prev: ToolActivityEntry[]) => ToolActivityEntry[]) => {
      toolActivityRef.current = next(toolActivityRef.current);
      setToolActivity(toolActivityRef.current);
    },
    [],
  );

  /**
   * Close out a turn: nothing left pulsing, and the run itself written onto the
   * answer it produced. Kept on the message rather than in provider state so
   * the next turn does not erase what the last one did.
   */
  const settleTurn = useCallback(
    (assistantMessageId: string, outcome: TurnOutcome, startedAt: number | null) => {
      if (!pageScope) return;
      // A cap can land after `tool_start` and before `tool_result`, so a tool
      // caught by it is cut, not finished. Recording it as done would be the
      // chip that never resolves, inverted — and this one is durable, because
      // it is written onto the message.
      applyTools((prev) =>
        settleTools(prev, outcome === 'capped' || outcome === 'awaiting' ? 'stopped' : outcome),
      );
      const liveTrace = toolActivityRef.current;
      store.updateMessage(pageScope, assistantMessageId, (message) => {
        let trace = liveTrace;
        if (message.writeApproval) {
          if (!trace.length) {
            trace = [{ name: message.writeApproval.toolName, status: 'stopped' }];
          }
          trace = trace.map((row, index, all) =>
            index === all.length - 1 ? { ...row, writeApproval: message.writeApproval } : row,
          );
        }
        return {
          ...message,
          trace: trace.length ? trace : undefined,
          traceDurationMs: startedAt === null ? undefined : Date.now() - startedAt,
          traceOutcome: outcome,
        };
      });
    },
    [applyTools, pageScope, store],
  );

  const cancelIssuePreparation = useCallback(() => {
    issuePreparationRef.current?.controller.abort();
    issuePreparationRef.current = null;
    preparationSubmitRef.current = null;
  }, []);

  const startIssuePreparation = useCallback(
    (issueId: string, force = false): IssuePreparationHandle | null => {
      if (!tenantId) return null;
      const current = issuePreparationRef.current;
      const currentReady =
        current?.preparation?.status === 'ready' &&
        Date.parse(current.preparation.expires_at) > Date.now();
      const currentInflight = current !== null && !current.rejected && current.preparation == null;
      if (
        !force &&
        current &&
        current.tenantId === tenantId &&
        current.issueId === issueId &&
        (currentInflight || currentReady)
      ) {
        return current;
      }
      current?.controller.abort();
      const controller = new AbortController();
      const handle: IssuePreparationHandle = {
        tenantId,
        issueId,
        controller,
        promise: prepareTechnicalAuditIssueChat(
          { tenant_id: tenantId, issue_id: issueId },
          controller.signal,
        ),
        preparation: null,
        rejected: false,
      };
      issuePreparationRef.current = handle;
      void handle.promise
        .then((preparation) => {
          handle.preparation = preparation;
          if (
            issuePreparationRef.current !== handle ||
            boundAuditIssueRef.current?.issue_id !== handle.issueId
          ) {
            return;
          }
          if (preparation.status !== 'ready') {
            setStatus(preparation.reason);
          }
        })
        .catch((error: unknown) => {
          handle.rejected = true;
          if (
            issuePreparationRef.current !== handle ||
            controller.signal.aborted ||
            boundAuditIssueRef.current?.issue_id !== handle.issueId
          ) {
            return;
          }
          setStatus(error instanceof Error ? error.message : 'Page preparation failed.');
        });
      return handle;
    },
    [tenantId],
  );

  const setIssueContext = useCallback(
    (attachment: CentralChatIssueAttachment) => {
      if (issuePreparationRef.current?.issueId !== attachment.issue_id) {
        cancelIssuePreparation();
      }
      boundAuditIssueRef.current = {
        context_type: 'technical_audit_issue',
        issue_id: attachment.issue_id,
        route: AUDIT_ISSUE_ROUTE,
        issue_type: attachment.issue_type,
        wordpress_id: attachment.wordpress_id ?? null,
        run_id: attachment.run_id ?? null,
      };
      setActiveIssue((prev) =>
        prev &&
        prev.issue_id === attachment.issue_id &&
        prev.issue_type === attachment.issue_type &&
        prev.page_title === attachment.page_title
          ? prev
          : attachment,
      );
    },
    [cancelIssuePreparation],
  );

  const clearIssueContext = useCallback(() => {
    cancelIssuePreparation();
    boundAuditIssueRef.current = null;
    setActiveIssue(null);
  }, [cancelIssuePreparation]);

  useEffect(() => {
    cancelIssuePreparation();
    boundAuditIssueRef.current = null;
    setActiveIssue(null);
    return cancelIssuePreparation;
  }, [cancelIssuePreparation, sessionKey]);

  // An issue binding belongs to the audit page it was opened from. Leaving that
  // page (a route change) drops it, so the chat resolves to the new page's
  // session instead of staying stuck on the issue's.
  const onAuditRoute = location.pathname.startsWith('/audit');
  useEffect(() => {
    if (!onAuditRoute) clearIssueContext();
  }, [onAuditRoute, clearIssueContext]);

  useEffect(() => {
    const conversationId = session.conversationId;
    if (!conversationId || !tenantId) return;
    registerTaApprovalSubmitter(async (decision, signal) => {
      const outcome = await settleTechnicalAuditApproval(
        {
          conversation_id: conversationId,
          tenant_id: tenantId,
          proposal_id: decision.proposalId,
          approval_request_id: decision.approvalRequestId,
          decision: decision.decision,
        },
        signal,
      );
      if (outcome.ok && pageScope) {
        const live = useCentralChatStore.getState();
        const current = live.getSession(pageScope);
        if (current.conversationId === conversationId) {
          const target = current.messages.find(
            (message) => message.taProposal?.proposalId === decision.proposalId,
          );
          if (target) {
            const restored = restoredFromTaOutcome(outcome, decision.decision);
            live.updateMessage(pageScope, target.id, (message) => {
              if (message.taProposal?.proposalId !== decision.proposalId) return message;
              const parked =
                message.content.trim() === 'Review the proposed paragraph changes below.' ||
                message.content.trim() === 'Emjae needs more detail before it can go on.';
              return {
                ...message,
                taProposalRestored: restored,
                awaiting: undefined,
                content: parked ? '' : message.content,
              };
            });
          }
        }
        if ('receipt' in outcome && outcome.receipt) {
          queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY }).catch(() => undefined);
        }
      }
      return outcome;
    }, conversationId);
    return () => registerTaApprovalSubmitter(null);
  }, [pageScope, queryClient, session.conversationId, tenantId]);

  // ── Client-executed page tools ──────────────────────────────────────
  //
  // Eve can hand a tool call back to the browser; the page executes it and the
  // result is posted up. The runtime owns the LIFECYCLE — pending, the 20s
  // deadline, exactly-once posting — and rides the existing trace pipeline for
  // rendering: a pending call is a running trace row, a settled one resolves
  // it, on both surfaces, with no new markup. Execution and transport plug in
  // through clientToolBridge.ts; with no executor registered every call
  // expires at its deadline and the turn continues instead of hanging.
  const clientToolsRef = useRef<ClientToolCall[]>([]);
  const clientToolTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const clientToolAbortsRef = useRef<Map<string, AbortController>>(new Map());
  const [clientToolCalls, setClientToolCalls] = useState<ClientToolCall[]>([]);

  const applyClientTools = useCallback(
    (next: ClientToolCall[]) => {
      clientToolsRef.current = next;
      setClientToolCalls(next);
    },
    [],
  );

  const finishClientTool = useCallback(
    (id: string, status: 'resolved' | 'expired', outcome?: { content?: unknown; failed?: string }) => {
      const { calls, post } = settleClientTool(clientToolsRef.current, id, status, outcome);
      if (post === null) return; // already terminal — the race's loser goes home
      applyClientTools(calls);
      const timer = clientToolTimersRef.current.get(id);
      if (timer) clearTimeout(timer);
      clientToolTimersRef.current.delete(id);
      clientToolAbortsRef.current.get(id)?.abort();
      clientToolAbortsRef.current.delete(id);
      const call = calls.find((entry) => entry.id === id);
      if (call) {
        applyTools((prev) =>
          resolveTool(
            prev,
            call.name,
            post.status === 'success' ? 'completed' : 'failed',
            // The reason the wire is told is the reason the row shows: the
            // timeout sentence, the orphan sentence, or the page's own words.
            post.status === 'success' ? undefined : post.message,
          ),
        );
      }
      void postClientToolResult(post);
    },
    [applyClientTools, applyTools],
  );

  /**
   * A tool_use handed to the browser. Liveness rule: a call is live while a
   * turn is streaming, or while the newest assistant message is parked
   * (`awaiting`) — that is the turn it belongs to. Anything else is an orphan
   * (a session rebuilt from history, a turn that no longer exists) and is born
   * expired: answered with an error once, never executed, never retried.
   */
  const handleClientToolUse = useCallback(
    (toolUse: { id: string; name: string; args: Record<string, unknown> }) => {
      const lastMessage = store.getSession(pageScope).messages.at(-1);
      const hasLiveTurn =
        turnRef.current !== null ||
        Boolean(lastMessage && lastMessage.role === 'assistant' && lastMessage.awaiting);
      const { calls, post } = beginClientTool(
        clientToolsRef.current,
        toolUse,
        Date.now(),
        hasLiveTurn,
      );
      if (calls === clientToolsRef.current) return; // duplicate id — stands as answered
      clientToolModeRef.current.set(toolUse.id, {
        pageScope: pageScope ?? '',
        mode: parkedTurnModeRef.current?.pageScope === pageScope
          ? parkedTurnModeRef.current.mode
          : DEFAULT_CHAT_ACCESS_MODE,
      });
      applyClientTools(calls);
      if (post) {
        // Born expired: an orphan is answered, shown as a failed row, and done.
        applyTools((prev) =>
          resolveTool(startTool(prev, toolUse.name), toolUse.name, 'failed', post.message),
        );
        void postClientToolResult(post);
        return;
      }
      applyTools((prev) => startTool(prev, toolUse.name));
      const call = calls[calls.length - 1];
      const timer = setTimeout(
        () => finishClientTool(toolUse.id, 'expired'),
        clientToolDeadline(call) - Date.now(),
      );
      clientToolTimersRef.current.set(toolUse.id, timer);
      const executor = getClientToolExecutor();
      if (executor) {
        const controller = new AbortController();
        clientToolAbortsRef.current.set(toolUse.id, controller);
        executor(toolUse, controller.signal).then(
          (content) => finishClientTool(toolUse.id, 'resolved', { content }),
          (error: unknown) =>
            finishClientTool(toolUse.id, 'resolved', {
              failed: error instanceof Error ? error.message : String(error),
            }),
        );
      }
    },
    [applyClientTools, applyTools, finishClientTool, pageScope, store],
  );

  useEffect(() => {
    const timers = clientToolTimersRef.current;
    const aborts = clientToolAbortsRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      for (const controller of aborts.values()) controller.abort();
    };
  }, []);

  const sandboxOutput = useSandboxActions((message) => {
    if (!pageScope) return;
    store.appendMessage(pageScope, createCentralChatMessage('assistant', message));
  });

  const refreshConversations = useCallback(async () => {
    if (!tenantId) {
      setConversations([]);
      return;
    }
    setConversationLoading(true);
    try {
      setConversations(await listChatHubConversations(tenantId));
    } catch {
      setConversations([]);
    } finally {
      setConversationLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  useEffect(() => {
    const prompt = searchParams.get('prompt');
    if (prompt && pageScope && prompt !== session.draft) {
      store.setDraft(pageScope, prompt);
    }
    // Only depend on stable values: URL search string and page scope.
    // searchParams object reference changes every render, so we use location.search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, pageScope]);

  useEffect(() => {
    const latestMessageId = messages.at(-1)?.id ?? null;
    const visible = uiState.bubbleOpen || location.pathname === '/chat';
    if (visible && pageScope && latestMessageId) {
      store.markSessionRead(pageScope, latestMessageId);
    }
  }, [location.pathname, messages, pageScope, store, uiState.bubbleOpen]);

  // NOTE: we deliberately do NOT rehydrate on cross-tab `storage` events.
  // `persist.rehydrate()` blindly replaces in-memory state with whatever is on
  // disk, which can be STALER than what this tab just rendered — with staging
  // open in multiple tabs it would overwrite a just-streamed reply (the reply
  // flashes in, then reverts to an empty "Thinking..." bubble). Each tab keeps
  // its own live session; localStorage still persists + restores on reload.

  useEffect(() => {
    return () => {
      turnRef.current?.controller.abort();
    };
  }, []);

  const handleEvent = useCallback(
    async (event: ChatHubStreamEvent, assistantMessageId: string, startedAt: number) => {
      if (!tenantId || !pageScope) return;
      if (event.conversation_id && event.conversation_id !== session.conversationId) {
        store.setConversationId(pageScope, event.conversation_id, sessionKey ?? undefined);
      }
      // Remember which page this conversation belongs to, so its history row can
      // say so later. Recorded under the tenant scope and the page the turn
      // started on (both captured in this closure), which is where the reply is
      // being written even if the user has since navigated away.
      if (event.conversation_id && sessionKey) {
        store.recordPageConversation(
          sessionKey,
          activePage.key,
          activePage.label,
          event.conversation_id,
        );
      }

      // A tool call handed to the BROWSER rides `data.client_tool_use` on ANY
      // event — additive, so a backend that never sends it changes nothing.
      // Shape: { id, name, args }.
      const clientToolUse = event.data?.client_tool_use;
      if (
        clientToolUse &&
        typeof clientToolUse === 'object' &&
        typeof (clientToolUse as { id?: unknown }).id === 'string' &&
        typeof (clientToolUse as { name?: unknown }).name === 'string'
      ) {
        const use = clientToolUse as { id: string; name: string; args?: Record<string, unknown> };
        handleClientToolUse({ id: use.id, name: use.name, args: use.args ?? {} });
      }

      if (event.type === 'agile_plan_results') {
        // What happened to each row of the approved Agile Plan card, with Emjae's first-person summary.
        const target = agilePlanTargetRef.current;
        if (target) {
          const results = Array.isArray(event.results)
            ? event.results.filter((r): r is AgilePlanRowResult => typeof r === 'object' && r !== null && typeof (r as AgilePlanRowResult).index === 'number')
            : [];
          store.updateMessage(pageScope, target, (message) => ({
            ...message,
            agilePlanResults: results,
            agilePlanResultText: typeof event.message === 'string' ? event.message : undefined,
          }));
        }
        return;
      }
      if (event.type === 'init') {
        setStatus(event.message || 'Connected');
        return;
      }
      if (event.type === 'thinking') {
        setStatus(event.message || "I'm thinking...");
        return;
      }
      if (event.type === 'tool_start') {
        const toolName = String(event.data?.tool_name || event.message || 'Tool running');
        applyTools((prev) => startTool(prev, toolName));
        setStatus(event.message || `I'm running ${toolName}`);
        return;
      }
      if (event.type === 'tool_result') {
        const toolName =
          typeof event.data?.tool_name === 'string' ? event.data.tool_name : undefined;
        const toolStatus =
          typeof event.data?.status === 'string' ? event.data.status : 'completed';
        const errorDetail =
          typeof event.data?.error_detail === 'string' ? event.data.error_detail : undefined;
        applyTools((prev) => resolveTool(prev, toolName, toolStatus, errorDetail));
        setStatus(event.message || 'I ran that tool');
        return;
      }
      if (event.type === 'state') {
        setStatus(event.message || 'Context attached');
        return;
      }
      if (event.type === 'action_requested') {
        const actions = offeredActions(event.data?.actions);
        if (actions) {
          store.updateMessage(pageScope, assistantMessageId, (message) => ({
            ...message,
            actions,
          }));
        }
        setStatus(event.message || 'Waiting for selection');
        return;
      }
      if (event.type === 'chunk') {
        const content = event.message || event.content || '';
        if (!content) return;
        store.upsertMessage(pageScope, assistantMessageId, 'assistant', (message) => ({
          ...message,
          content: `${message.content}${content}`,
        }));
        return;
      }
      if (event.type === 'ui') {
        return;
      }
      if (event.type === 'complete') {
        const actions = offeredActions(event.data?.actions);
        const quickReplies = Array.isArray(event.data?.quick_replies)
          ? (event.data.quick_replies as unknown[]).filter(
              (reply): reply is string => typeof reply === 'string',
            )
          : undefined;
        const proposalId =
          typeof event.data?.proposal_id === 'string' ? event.data.proposal_id : undefined;
        // A parked turn arrives as `complete` because the transport has nothing
        // else to say, but it is a question, not an answer. Keep that on the
        // message so it does not read as finished work.
        const awaiting = typeof event.data?.awaiting === 'string' ? event.data.awaiting : undefined;
        const writeApproval = writeApprovalFromPayload(
          event.data?.write_approval && typeof event.data.write_approval === 'object'
            ? (event.data.write_approval as Record<string, unknown>)
            : undefined,
        );
        // The backend cuts a turn at its own time limit and sends what it has,
        // notice already appended to the text. That is a short answer, so it
        // arrives and renders as one; only the trace says it was cut.
        const capped = isTurnCapped(event.data);
        const taProposal = parseTaProposal(event.data?.ta_proposal);
        const proposalConversationId =
          typeof event.conversation_id === 'string' && event.conversation_id
            ? event.conversation_id
            : null;
        if (taProposal && proposalConversationId) {
          markTaProposalDelivered(taProposal.proposalId, proposalConversationId);
        }
        const question = questionFromPayload(event.data?.question);
        store.upsertMessage(pageScope, assistantMessageId, 'assistant', (message) => ({
          ...message,
          content: event.message || message.content || 'Done.',
          actions: actions ?? message.actions,
          quickReplies: quickReplies?.length ? quickReplies : undefined,
          reportProposalId: proposalId ?? message.reportProposalId,
          awaiting: awaiting ?? message.awaiting,
          writeApproval: writeApproval ?? message.writeApproval,
          question: question ?? message.question,
          taProposal: taProposal ?? message.taProposal,
        }));
        settleTurn(assistantMessageId, capped ? 'capped' : awaiting ? 'awaiting' : 'done', startedAt);
        if (capped) setStatus(turnCapStatus(event.data?.limit_secs));
        else setStatus(awaiting ? awaitingLabel(awaiting) : "I'm done");
        refreshConversations();
        return;
      }
      if (event.type === 'error') {
        // A cap that catches a turn before it wrote anything arrives as `error`
        // rather than `complete` (chat_hub.py: `"complete" if assistant_text
        // else "error"`), carrying the same `stopped_by` marker and a plain
        // sentence explaining itself. Running that through the error path put
        // that sentence in a red alert under the words "I hit an error while
        // working on that" — a failure Emjae did not have, over an explanation
        // that reads perfectly well as the answer.
        if (isTurnCapped(event.data)) {
          store.upsertMessage(pageScope, assistantMessageId, 'assistant', (message) => ({
            ...message,
            content: event.message || message.content || 'That turn hit the time limit.',
          }));
          settleTurn(assistantMessageId, 'capped', startedAt);
          setStatus(turnCapStatus(event.data?.limit_secs));
          refreshConversations();
          return;
        }
        store.upsertMessage(pageScope, assistantMessageId, 'assistant', (message) => ({
          ...message,
          content: message.content || 'I hit an error while working on that.',
          error: event.message || 'Chat hub error',
        }));
        settleTurn(assistantMessageId, 'error', startedAt);
        setStatus(event.message || 'I hit an error');
      }
    },
    [
      activePage.key,
      activePage.label,
      applyTools,
      handleClientToolUse,
      pageScope,
      refreshConversations,
      sandboxOutput,
      session.conversationId,
      sessionKey,
      settleTurn,
      store,
      tenantId,
    ],
  );

  // Emjae asked a question (ask_question) and is waiting on the person. The answer goes to the answer
  // route for that request id, not to a new turn; the question clears at once so it cannot be answered twice.
  const answeringQuestionRef = useRef<string | null>(null);
  const answerQuestion = useCallback(
    (answer: { optionId: string } | { text: string }) => {
      void (async () => {
        if (!pageScope || !tenantId || !session.conversationId || isStreaming) return;
        const pending = [...messages].reverse().find((message) => message.question);
        const question = pending?.question;
        if (!pending || !question || answeringQuestionRef.current === question.requestId) return;
        const shown =
          'optionId' in answer
            ? question.options.find((option) => option.id === answer.optionId)?.label
            : answer.text.trim();
        if (!shown) return;
        answeringQuestionRef.current = question.requestId;
        store.updateMessage(pageScope, pending.id, (message) => ({
          ...message,
          question: undefined,
          awaiting: undefined,
        }));
        store.appendMessage(pageScope, createCentralChatMessage('user', shown));
        if (turnRef.current) {
          turnRef.current.ending = 'replaced';
          turnRef.current.controller.abort();
          await turnRef.current.settled;
        }
        const controller = new AbortController();
        const startedAt = Date.now();
        const turn = beginTurn(controller, startedAt);
        turnRef.current = turn;
        setIsStreaming(true);
        setTurnStartedAt(startedAt);
        const assistantMessage = createCentralChatMessage('assistant', '');
        store.appendMessage(pageScope, assistantMessage);
        try {
          await answerChatHubQuestion(
            session.conversationId,
            question.requestId,
            'optionId' in answer ? { optionId: answer.optionId } : { text: answer.text.trim() },
            tenantId,
            {
              signal: controller.signal,
              onEvent: (event) => {
                void handleEvent(event, assistantMessage.id, startedAt);
              },
            },
          );
        } catch (error) {
          if (controller.signal.aborted) {
            if (turn.ending === 'user') {
              store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
                ...assistant,
                content: stoppedContent(assistant.content),
              }));
              settleTurn(assistantMessage.id, 'stopped', startedAt);
              setStatus('Stopped');
            }
            return;
          }
          const message = error instanceof Error ? error.message : 'Chat request failed';
          store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
            ...assistant,
            content: assistant.content || 'I could not complete that request.',
            error: message,
          }));
          settleTurn(assistantMessage.id, 'error', startedAt);
          toast.error(message);
        } finally {
          if (turnRef.current === turn) {
            turnRef.current = null;
            setIsStreaming(false);
            setTurnStartedAt(null);
          }
          turn.markSettled();
        }
      })();
    },
    [handleEvent, isStreaming, messages, pageScope, session.conversationId, settleTurn, store, tenantId],
  );

  const submit = useCallback(
    async (text: string, contextOverride?: Partial<CentralChatContext>) => {
      const trimmed = text.trim();
      if (!trimmed || !tenantId || !pageScope || isStreaming) return;
      // A typed message while Emjae's question allows a typed answer IS the answer, not a new turn.
      if ([...messages].reverse().find((message) => message.question)?.question?.allowFreeform) {
        store.setDraft(pageScope, '');
        answerQuestion({ text: trimmed });
        return;
      }
      const turnChatAccessMode = store.getChatAccessMode(sessionKey, pageScope);

      let issuePreparation: ReadyIssuePreparation | null = null;
      const requestedIssue = boundAuditIssueFrom(contextOverride) ?? boundAuditIssueRef.current;
      if (requestedIssue) {
        if (preparationSubmitRef.current) return;
        const preparationSubmit = Symbol(requestedIssue.issue_id);
        preparationSubmitRef.current = preparationSubmit;
        let handle = startIssuePreparation(requestedIssue.issue_id);
        if (!handle) {
          preparationSubmitRef.current = null;
          return;
        }
        setStatus('Preparing page context');
        try {
          let preparation = await handle.promise;
          if (
            preparationSubmitRef.current !== preparationSubmit ||
            issuePreparationRef.current !== handle
          ) {
            return;
          }
          if (preparation.status === 'ready' && Date.parse(preparation.expires_at) <= Date.now()) {
            setStatus('Refreshing page context');
            const refreshedHandle = startIssuePreparation(requestedIssue.issue_id, true);
            if (!refreshedHandle) return;
            handle = refreshedHandle;
            preparation = await handle.promise;
            if (
              preparationSubmitRef.current !== preparationSubmit ||
              issuePreparationRef.current !== handle
            ) {
              return;
            }
          }
          if (preparation.status !== 'ready') {
            setStatus(preparation.reason);
            toast.error(preparation.reason);
            return;
          }
          if (Date.parse(preparation.expires_at) <= Date.now()) {
            const message = 'Page context expired before it could be used. Send again.';
            setStatus(message);
            toast.error(message);
            return;
          }
          issuePreparation = preparation;
        } catch (error) {
          if (
            preparationSubmitRef.current !== preparationSubmit ||
            issuePreparationRef.current !== handle ||
            handle.controller.signal.aborted
          ) {
            return;
          }
          const message = error instanceof Error ? error.message : 'Page preparation failed.';
          setStatus(message);
          toast.error(message);
          return;
        } finally {
          if (preparationSubmitRef.current === preparationSubmit) {
            preparationSubmitRef.current = null;
          }
        }
      }

      const userMessage = createCentralChatMessage('user', trimmed);
      const assistantMessage = createCentralChatMessage('assistant', '');
      const history = compactHistory(messages);

      store.appendMessage(pageScope, userMessage);
      store.appendMessage(pageScope, assistantMessage);
      store.setDraft(pageScope, '');
      const startedAt = Date.now();
      parkedTurnModeRef.current = { pageScope, mode: turnChatAccessMode };
      setIsStreaming(true);
      setStatus("I'm starting...");
      applyTools(() => []);
      setTurnStartedAt(startedAt);

      if (turnRef.current) {
        turnRef.current.ending = 'replaced';
        turnRef.current.controller.abort();
      }
      const controller = new AbortController();
      const turn = beginTurn(controller, startedAt);
      turnRef.current = turn;

      // Idle watchdog: if the stream goes silent (a stalled backend or an SSE the
      // edge quietly dropped), abort it so `isStreaming` can't get stuck `true` and
      // wedge the composer — every later send would otherwise no-op at the guard
      // above, leaving the draft in the box with no animation. Reset on each event;
      // the backend heartbeats keep a healthy long generation from tripping it.
      //
      // That heartbeat contract is load-bearing, not a nicety. A server step
      // that works in SILENCE (an LLM call) trips this watchdog, and the user
      // sees "timed out" even though the answer — or the deterministic
      // fallback that exists for exactly that case — was on its way. The ads
      // paths shipped without heartbeats and did precisely that in staging.
      // Server side the caps are _ADS_LLM_HEARTBEAT_SECS / _ADS_LLM_MAX_WAIT_SECS
      // in chat_hub.py, deliberately below this value; lowering this number
      // without lowering those reopens the same race.
      //
      // Heartbeats reset it too, so this timer now only catches a stream that
      // has stopped entirely. Silence UNDER a live heartbeat is a different
      // failure and is counted separately below, because a beat proves the
      // socket is open and nothing about whether the agent is making progress.
      const IDLE_TIMEOUT_MS = 90_000;
      let idleTimer: ReturnType<typeof setTimeout> | undefined;
      const resetIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          turn.ending = 'idle';
          controller.abort();
        }, IDLE_TIMEOUT_MS);
      };
      resetIdle();

      let consecutiveHeartbeats = 0;

      try {
        const contextBase = contextOverride?.context_type
          ? buildCentralChatContext({
              tenantId,
              pathname: location.pathname,
              searchParams: new URLSearchParams(location.search),
              overrideContextType: contextOverride.context_type,
            })
          : baseContext;
        const requestContext = {
          ...contextBase,
          ...contextOverride,
          capabilities: {
            ...contextBase.capabilities,
            ...contextOverride?.capabilities,
          },
        };
        const captured =
          boundAuditIssueFrom(contextOverride) ?? boundAuditIssueFrom(requestContext);
        if (captured) {
          boundAuditIssueRef.current = captured;
        }
        const boundIssue = boundAuditIssueRef.current;
        const streamContext = boundIssue
          ? withBoundAuditIssue(requestContext, boundIssue)
          : requestContext;
        const preparedStreamContext = issuePreparation
          ? bindTechnicalAuditIssuePreparation(streamContext, issuePreparation)
          : streamContext;
        // Only the template builder reads these. Every other context either
        // runs Emjae, which owns its own model inside Eve, or a service that
        // picks its own — sending a model there wrote a value onto the
        // conversation record that nothing ever ran.
        const modelConfig = MODEL_CHOOSING_CONTEXT_TYPES.has(preparedStreamContext.context_type)
          ? {
              model: CENTRAL_CHAT_MODEL,
              provider: CENTRAL_CHAT_PROVIDER,
            }
          : {};
        await streamChatHub(
          {
            tenant_id: tenantId,
            chat_mode: turnChatAccessMode,
            message: trimmed,
            conversation_id: session.conversationId,
            conversation_history: history,
            context: preparedStreamContext,
            ...modelConfig,
            enable_web_research: preparedStreamContext.capabilities.can_use_web_research,
            enable_image_generation: preparedStreamContext.capabilities.can_generate_images,
            enable_seo_analysis: true,
            // What THIS page can run, read as the body is built. Empty on
            // every browser without the agent surface, and an empty list is
            // what tells the backend to describe no page tools at all.
            page_tools: currentPageTools(),
          },
          {
            signal: controller.signal,
            onEvent: (event) => {
              resetIdle();
              if (!isHeartbeatEvent(event)) {
                consecutiveHeartbeats = 0;
                void handleEvent(event, assistantMessage.id, startedAt);
                return;
              }
              // A beat carries nothing for the transcript, so it never reaches
              // handleEvent. It only moves the silence counter.
              consecutiveHeartbeats += 1;
              if (consecutiveHeartbeats >= STALL_HEARTBEAT_LIMIT) {
                turn.ending = 'stalled';
                controller.abort();
                return;
              }
              setStatus(silenceStatus(consecutiveHeartbeats) ?? event.message ?? "I'm still working");
            },
          },
        );
      } catch (error) {
        if (controller.signal.aborted) {
          // A fresh submit or an unmount aborts the prior stream on purpose and
          // says nothing. Every other ending leaves the transcript explaining
          // itself, because a discarded turn the user cannot account for is the
          // same lie as a chip that never resolves.
          if (turn.ending === 'user') {
            store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
              ...assistant,
              content: stoppedContent(assistant.content),
            }));
            settleTurn(assistantMessage.id, 'stopped', startedAt);
            setStatus('Stopped');
            return;
          }
          if (turn.ending === 'stalled' || turn.ending === 'idle') {
            const message =
              turn.ending === 'stalled' ? STALL_MESSAGE : 'The response timed out — please try again.';
            store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
              ...assistant,
              content: assistant.content || 'I could not complete that request.',
              error: message,
            }));
            settleTurn(assistantMessage.id, 'error', startedAt);
            setStatus(message);
            toast.error(message);
          }
          return;
        }
        if (isChatHubPreparationConflict(error) && issuePreparation) {
          const preparationHandle = issuePreparationRef.current;
          if (
            boundAuditIssueRef.current?.issue_id !== issuePreparation.issue_id ||
            preparationHandle?.tenantId !== tenantId ||
            preparationHandle.issueId !== issuePreparation.issue_id ||
            preparationHandle.preparation?.status !== 'ready' ||
            preparationHandle.preparation.preparation_id !== issuePreparation.preparation_id
          ) {
            return;
          }
          store.replaceMessages(pageScope, session.conversationId, messages);
          store.setDraft(pageScope, trimmed);
          const issueId = issuePreparation.issue_id;
          cancelIssuePreparation();
          setStatus('Page context changed; preparing it again');
          startIssuePreparation(issueId, true);
          toast.error('The page context changed. Review your message and send it again.');
          return;
        }
        const message = error instanceof Error ? error.message : 'Chat request failed';
        store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
          ...assistant,
          content: assistant.content || 'I could not complete that request.',
          error: message,
        }));
        settleTurn(assistantMessage.id, 'error', startedAt);
        toast.error(message);
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        // Only the turn that still owns the runtime may tear it down. A turn
        // aborted as `replaced` settles after its replacement has already set
        // the clock and the streaming flag, and clearing them here would stop
        // a live turn's clock and unwedge a composer that is legitimately busy.
        if (turnRef.current === turn) {
          turnRef.current = null;
          setIsStreaming(false);
          setTurnStartedAt(null);
        }
        // Last, so anything waiting on this turn wakes to a runtime that is
        // already torn down rather than to one mid-teardown.
        turn.markSettled();
      }
    },
    [
      answerQuestion,
      applyTools,
      baseContext,
      cancelIssuePreparation,
      handleEvent,
      isStreaming,
      location.pathname,
      location.search,
      messages,
      pageScope,
      session.conversationId,
      sessionKey,
      settleTurn,
      startIssuePreparation,
      store,
      tenantId,
    ],
  );

  /**
   * A page tool's answer, going back to the turn that parked waiting for it.
   *
   * This is the third half of the clientToolBridge seam, and it lives here
   * rather than in the WebMCP lane because it is the only one that needs the
   * conversation and the stream handler. Eve is still holding that turn open;
   * the same `/chat-hub/stream` route carries the answer in, and the reply
   * comes back down the stream every other turn uses, through the same event
   * handler, so the answer renders with no rendering code of its own.
   *
   * No user message: the transcript already shows the question and the parked
   * answer above this one. Only a fresh assistant bubble for what Emjae says
   * once it has the data.
   */
  const postResult = useCallback(
    async (result: ClientToolResult) => {
      if (!tenantId || !pageScope) return;
      const toolMode = clientToolModeRef.current.get(result.tool_use_id);
      clientToolModeRef.current.delete(result.tool_use_id);
      const turnChatAccessMode = toolMode?.pageScope === pageScope
        ? toolMode.mode
        : DEFAULT_CHAT_ACCESS_MODE;
      // The turn that asked is still closing. Its Eve cursor is saved during
      // that teardown, and resuming before it lands replays the park.
      await turnRef.current?.settled;

      const assistantMessage = createCentralChatMessage('assistant', '');
      store.appendMessage(pageScope, assistantMessage);
      const startedAt = Date.now();
      setIsStreaming(true);
      setStatus('Reading what the page sent back');
      setTurnStartedAt(startedAt);

      const controller = new AbortController();
      const turn = beginTurn(controller, startedAt);
      turnRef.current = turn;
      // Same watchdog reasoning as `submit`: a stream that dies silently must
      // not leave `isStreaming` stuck true and wedge the composer.
      let idleTimer: ReturnType<typeof setTimeout> | undefined;
      const resetIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          turn.ending = 'idle';
          controller.abort();
        }, 90_000);
      };
      resetIdle();

      try {
        await streamChatHub(
          {
            tenant_id: tenantId,
            chat_mode: turnChatAccessMode,
            message: '',
            conversation_id: session.conversationId,
            conversation_history: [],
            context: baseContext,
            client_tool_result: result,
          },
          {
            signal: controller.signal,
            onEvent: (event) => {
              resetIdle();
              if (isHeartbeatEvent(event)) return;
              void handleEvent(event, assistantMessage.id, startedAt);
            },
          },
        );
      } catch (error) {
        // A turn discarded without explanation is the same lie as a chip that
        // never resolves, so this one explains itself too.
        if (turn.ending === 'user') return;
        const message = error instanceof Error ? error.message : 'Chat request failed';
        store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
          ...assistant,
          content: assistant.content || 'I could not finish reading what the page sent back.',
          error: message,
        }));
        settleTurn(assistantMessage.id, 'error', startedAt);
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        if (turnRef.current === turn) {
          turnRef.current = null;
          setIsStreaming(false);
          setTurnStartedAt(null);
        }
        turn.markSettled();
      }
    },
    [baseContext, handleEvent, pageScope, session.conversationId, settleTurn, store, tenantId],
  );

  useEffect(() => {
    registerClientToolResultPoster(postResult);
    // Cleared rather than left pointing at a torn-down runtime: with no poster
    // registered the bridge warns and drops, which is the honest floor, and a
    // stale closure would write into a session that no longer exists.
    return () => registerClientToolResultPoster(null);
  }, [postResult]);

  const stop = useCallback(() => {
    const turn = turnRef.current;
    if (!turn) return;
    turn.ending = 'user';
    turn.controller.abort();
    // The abort above ends the READ. Eve keeps working — and billing — until
    // the turn cap unless told to stop, so Stop also stops the meter. Fire and
    // forget: both of Eve's outcomes are success and the local stop already
    // rendered.
    if (session.conversationId && tenantId) {
      void cancelChatHubTurn(session.conversationId, tenantId);
    }
  }, [session.conversationId, tenantId]);

  const settleWriteApproval = useCallback(
    (decision: 'approve' | 'cancel') => {
      void (async () => {
        if (!pageScope || !tenantId || !session.conversationId) return;
        // A card already settling, or already showing its results, takes no second settle (either decision).
        const pending = [...messages].reverse().find((message) => message.writeApproval && !message.settling && !message.agilePlanResults);
        if (!pending?.writeApproval || settlingRef.current) return;
        settlingRef.current = true;
        const requestId = pending.writeApproval.requestId;
        // An approved Agile Plan card stays, so the per-row results can land on it, marked settling so no live button
        // remains; everything else clears at once.
        const keepForResults = decision === 'approve' && isAgilePlanApproval(pending.writeApproval);
        agilePlanTargetRef.current = keepForResults ? pending.id : null;
        if (keepForResults) {
          store.updateMessage(pageScope, pending.id, (message) => ({ ...message, settling: true }));
        } else {
          store.updateMessage(pageScope, pending.id, (message) => ({
            ...message,
            writeApproval: undefined,
          }));
        }
        if (turnRef.current) {
          turnRef.current.ending = 'replaced';
          turnRef.current.controller.abort();
          await turnRef.current.settled;
        }
        const controller = new AbortController();
        const startedAt = Date.now();
        const turn = beginTurn(controller, startedAt);
        turnRef.current = turn;
        setIsStreaming(true);
        setTurnStartedAt(startedAt);
        const assistantMessage = createCentralChatMessage('assistant', '');
        store.appendMessage(pageScope, assistantMessage);
        try {
          await settleChatHubApproval(
            session.conversationId,
            requestId,
            decision,
            tenantId,
            {
              signal: controller.signal,
              onEvent: (event) => {
                void handleEvent(event, assistantMessage.id, startedAt);
              },
            },
          );
        } catch (error) {
          if (controller.signal.aborted) {
            if (turn.ending === 'user') {
              store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
                ...assistant,
                content: stoppedContent(assistant.content),
              }));
              settleTurn(assistantMessage.id, 'stopped', startedAt);
              setStatus('Stopped');
            }
            return;
          }
          const message = error instanceof Error ? error.message : 'Chat request failed';
          store.updateMessage(pageScope, assistantMessage.id, (assistant) => ({
            ...assistant,
            content: assistant.content || 'I could not complete that request.',
            error: message,
          }));
          settleTurn(assistantMessage.id, 'error', startedAt);
          toast.error(message);
        } finally {
          // An approved Agile Plan card kept for its results: if none arrived (Core drained the approval, or the
          // request failed) the card is spent, so it must not keep a live Approve button.
          settlingRef.current = false;
          const kept = agilePlanTargetRef.current;
          agilePlanTargetRef.current = null;
          if (kept) {
            store.updateMessage(pageScope, kept, (message) =>
              message.agilePlanResults ? message : { ...message, writeApproval: undefined },
            );
          }
          if (turnRef.current === turn) {
            turnRef.current = null;
            setIsStreaming(false);
            setTurnStartedAt(null);
          }
          turn.markSettled();
        }
      })();
    },
    [handleEvent, messages, pageScope, session.conversationId, settleTurn, store, tenantId],
  );

  const openConversation = useCallback(
    async (conversationId: string) => {
      if (!pageScope) return;
      parkedTurnModeRef.current = null;
      clientToolModeRef.current.clear();
      cancelIssuePreparation();
      boundAuditIssueRef.current = null;
      setActiveIssue(null);
      try {
        const detail = await getChatHubConversation(conversationId);
        const pending = writeApprovalFromPayload(
          detail.pending_write_approval as Record<string, unknown> | null | undefined,
        );
        const pendingQuestion = questionFromPayload(detail.pending_question);
        const loadedMessages = detail.messages
          .filter((message) => message.role === 'user' || message.role === 'assistant')
          .map((message, index, all): CentralChatMessage => {
            const lastAssistant =
              pending &&
              message.role === 'assistant' &&
              all.slice(index + 1).every((later) => later.role !== 'assistant');
            const lastAssistantQuestion =
              pendingQuestion &&
              message.role === 'assistant' &&
              all.slice(index + 1).every((later) => later.role !== 'assistant');
            const stored = parseRestoredTaProposal(
              message.proposal_data,
              message.proposal_status,
            );
            if (stored && stored.restored === null) {
              forgetTaProposalDelivery(stored.proposal.proposalId);
            }
            return {
              id: message.id,
              role: message.role as 'user' | 'assistant',
              content: message.content,
              createdAt: message.created_at || new Date().toISOString(),
              awaiting: lastAssistant ? 'write_approval' : lastAssistantQuestion ? 'question' : undefined,
              question: lastAssistantQuestion ? pendingQuestion : undefined,
              writeApproval: lastAssistant ? pending : undefined,
              trace:
                lastAssistant && pending
                  ? [{ name: pending.toolName, status: 'stopped', writeApproval: pending }]
                  : undefined,
              taProposal: stored?.proposal,
              taProposalRestored: stored?.restored ?? undefined,
            };
          });
        store.replaceMessages(pageScope, detail.id, loadedMessages);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not load conversation';
        toast.error(message);
      }
    },
    [cancelIssuePreparation, pageScope, store],
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await deleteChatHubConversation(conversationId);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Could not delete conversation';
        toast.error(message);
        return;
      }
      // If the deleted thread is the one currently on screen, clear the panel
      // back to a fresh chat so we don't keep showing a deleted conversation.
      if (pageScope && session.conversationId === conversationId) {
        parkedTurnModeRef.current = null;
        clientToolModeRef.current.clear();
        cancelIssuePreparation();
        boundAuditIssueRef.current = null;
        setActiveIssue(null);
        store.resetSession(pageScope);
      }
      if (sessionKey) store.deleteChatAccessMode(sessionKey, conversationId);
      await refreshConversations();
    },
    [cancelIssuePreparation, pageScope, refreshConversations, session.conversationId, sessionKey, store],
  );

  const startNew = useCallback(() => {
    if (!pageScope) return;
    parkedTurnModeRef.current = null;
    clientToolModeRef.current.clear();
    cancelIssuePreparation();
    if (turnRef.current) {
      turnRef.current.ending = 'replaced';
      turnRef.current.controller.abort();
    }
    boundAuditIssueRef.current = null;
    setActiveIssue(null);
    store.resetSession(pageScope);
    setStatus('');
    applyTools(() => []);
    setTurnStartedAt(null);
    setIsStreaming(false);
  }, [applyTools, cancelIssuePreparation, pageScope, store]);

  const setDraft = useCallback(
    (nextDraft: string) => {
      if (!pageScope) return;
      store.setDraft(pageScope, nextDraft);
    },
    [pageScope, store],
  );

  const setChatAccessMode = useCallback(
    (mode: ChatAccessMode) => {
      if (!sessionKey || !pageScope) return;
      store.setChatAccessMode(sessionKey, pageScope, mode);
    },
    [pageScope, sessionKey, store],
  );

  const listedConversations = useMemo<ListedConversation[]>(() => {
    // Attach the page each conversation belongs to: first the client-side page
    // index (accurate, and the only source for issue pages, whose page id the
    // backend row does not carry), then a fallback derived from the row's route.
    const labelFor = (conv: ChatHubConversation): { pageKey?: string; pageLabel?: string } => {
      for (const [pageKey, entry] of Object.entries(pages)) {
        if (entry.conversationIds.includes(conv.id)) return { pageKey, pageLabel: entry.label };
      }
      const derived = conversationPage(conv);
      return derived ? { pageKey: derived.key, pageLabel: derived.label } : {};
    };
    const decorated: ListedConversation[] = conversations.map((conv) => ({
      ...conv,
      ...labelFor(conv),
    }));
    const id = session.conversationId;
    if (!id || !tenantId || messages.length === 0) return decorated;
    if (decorated.some((conversation) => conversation.id === id)) return decorated;
    return [
      {
        id,
        tenant_id: tenantId,
        title: messages.find((message) => message.role === 'user')?.content ?? null,
        message_count: messages.length,
        last_message_at: messages.at(-1)?.createdAt ?? null,
        pageKey: activePage.key,
        pageLabel: activePage.label,
      },
      ...decorated,
    ];
  }, [activePage.key, activePage.label, conversations, messages, pages, session.conversationId, tenantId]);

  const [openNonce, setOpenNonce] = useState(0);

  const openBubble = useCallback(() => {
    if (!sessionKey || !pageScope) return;
    store.setBubbleOpen(sessionKey, true);
    setOpenNonce((n) => n + 1);
    store.markSessionRead(pageScope, messages.at(-1)?.id ?? null);
  }, [messages, pageScope, sessionKey, store]);

  const openIssueChat = useCallback(
    (attachment: CentralChatIssueAttachment, draft: string) => {
      if (!sessionKey) return;
      cancelIssuePreparation();
      // The prior page's turn is abandoned (it keeps streaming into its own
      // session; we just stop reading it here) so this page's composer is free.
      if (turnRef.current) {
        turnRef.current.ending = 'replaced';
        turnRef.current.controller.abort();
      }
      applyTools(() => []);
      setTurnStartedAt(null);
      setIsStreaming(false);
      setStatus('');
      boundAuditIssueRef.current = {
        context_type: 'technical_audit_issue',
        issue_id: attachment.issue_id,
        route: AUDIT_ISSUE_ROUTE,
        issue_type: attachment.issue_type,
        wordpress_id: attachment.wordpress_id ?? null,
        run_id: attachment.run_id ?? null,
      };
      // Binding to this issue switches the live page to its client page and
      // RESUMES that page's latest session — every issue on the same page shares
      // one thread, so we do not reset it. A provided draft prefills its composer
      // without clobbering an in-progress one.
      setActiveIssue(attachment);
      const issueScope = pageScopeKey(sessionKey, issuePage(attachment).key);
      if (issueScope && draft) store.setDraft(issueScope, draft);
      setStatus('Preparing page context');
      startIssuePreparation(attachment.issue_id, true);
      store.setBubbleOpen(sessionKey, true);
      setOpenNonce((n) => n + 1);
    },
    [applyTools, cancelIssuePreparation, sessionKey, startIssuePreparation, store],
  );

  const closeBubble = useCallback(() => {
    if (!sessionKey) return;
    store.setBubbleOpen(sessionKey, false);
  }, [sessionKey, store]);

  const openFullChat = useCallback(() => {
    const href = location.pathname === '/chat'
      ? '/chat'
      : buildCentralChatHref({ route: currentRoute(location.pathname, location.search) });
    if (sessionKey) store.setBubbleOpen(sessionKey, false);
    navigate(href);
  }, [location.pathname, location.search, navigate, sessionKey, store]);

  const value = useMemo<CentralChatRuntimeValue>(
    () => ({
      tenantId,
      sessionKey,
      activePage,
      contextLabel: baseContext.context_type.replace('_', ' '),
      draft: session.draft,
      status,
      isStreaming,
      conversationLoading,
      activeConversationId: session.conversationId,
      chatAccessMode,
      messages,
      conversations: listedConversations,
      toolActivity,
      turnStartedAt,
      clientToolCalls,
      handleClientToolUse,
      pathname: location.pathname,
      clientName,
      modes: CENTRAL_CHAT_MODES,
      workflowTiles: CENTRAL_CHAT_WORKFLOW_TILES,
      helpText: CENTRAL_CHAT_HELP_TEXT,
      bubbleOpen: uiState.bubbleOpen,
      openNonce,
      unreadCount: unreadAssistantCount(messages, session.lastReadMessageId),
      isFullChatRoute: location.pathname === '/chat',
      setDraft,
      setChatAccessMode,
      submit: (text, contextOverride) => {
        void submit(text, contextOverride);
      },
      stop,
      handleWorkflow: (tile) => {
        void submit(tile.prompt, tile.contextType ? { context_type: tile.contextType } : undefined);
      },
      handleMode: (mode) => setDraft(mode.prompt),
      openConversation: (conversationId) => {
        void openConversation(conversationId);
      },
      refreshConversations: () => {
        void refreshConversations();
      },
      deleteConversation: (conversationId) => {
        void deleteConversation(conversationId);
      },
      startNew,
      openBubble,
      closeBubble,
      openFullChat,
      goHome: () => navigate('/dashboard'),
      sandboxOutput,
      settleWriteApproval,
      answerQuestion,
      setIssueContext,
      clearIssueContext,
      activeIssue,
      openIssueChat,
    }),
    [
      activeIssue,
      activePage,
      baseContext.context_type,
      chatAccessMode,
      clearIssueContext,
      closeBubble,
      conversationLoading,
      listedConversations,
      deleteConversation,
      isStreaming,
      location.pathname,
      messages,
      navigate,
      openBubble,
      openConversation,
      openFullChat,
      openIssueChat,
      refreshConversations,
      setIssueContext,
      sandboxOutput,
      session.conversationId,
      session.draft,
      session.lastReadMessageId,
      sessionKey,
      setDraft,
      setChatAccessMode,
      startNew,
      status,
      settleWriteApproval,
      answerQuestion,
      stop,
      submit,
      tenantId,
      toolActivity,
      turnStartedAt,
      clientToolCalls,
      handleClientToolUse,
      openNonce,
      uiState.bubbleOpen,
      clientName,
    ],
  );

  return (
    <CentralChatRuntimeContext.Provider value={value}>
      {children}
    </CentralChatRuntimeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCentralChatRuntime(): CentralChatRuntimeValue {
  const context = useContext(CentralChatRuntimeContext);
  if (!context) {
    throw new Error('useCentralChatRuntime must be used inside CentralChatProvider');
  }
  return context;
}
